import { agentUrl } from '../hooks/useStreamChat';
import { AgentChatMessage, agentChatMessageSchema } from '../types';

interface SubscribeToChatParams {
  token: string;
  payload: unknown;
  maxRetries: number;
  retryDelay: number;
  onMessage: (chunk: AgentChatMessage) => void;
  onError?: (err: unknown) => void;
  onComplete?: () => void;
}

/**
 * Streams chat chunks using the Fetch API + ReadableStream, without RxJS Observables.
 * Returns an RxJS Subscription so the caller can reuse existing cleanup code.
 * Unsubscribing aborts the underlying request via AbortController.
 */
export const subscribeToChat = ({
  token,
  payload,
  maxRetries,
  retryDelay,
  onMessage,
  onError = (err) => console.error('Chat stream error', err),
  onComplete,
}: SubscribeToChatParams): AbortController => {
  const abortController = new AbortController();

  const run = async () => {
    let attempt = 0;

    // Helper to perform a single fetch + stream cycle
    const execute = async () => {
      const res = await fetch(`${agentUrl}/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch from agent: ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) {
        throw new Error('ReadableStream not supported');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const chunk = agentChatMessageSchema.parse(JSON.parse(line));
            onMessage(chunk);
          } catch (err) {
            console.error('Error parsing chat chunk', err, line);
          }
        }
      }

      // Flush any remaining buffered data
      if (buffer.trim()) {
        try {
          const chunk = agentChatMessageSchema.parse(JSON.parse(buffer));
          onMessage(chunk);
        } catch (err) {
          console.warn('Error parsing final chat chunk', err);
        }
      }

      onComplete?.();
    };

    // Retry loop
    while (attempt < maxRetries && !abortController.signal.aborted) {
      try {
        await execute();
        // Success → exit retry loop
        return;
      } catch (err) {
        if (abortController.signal.aborted) return; // Encountered during abort

        attempt += 1;
        if (attempt >= maxRetries) {
          onError(err);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }
  };

  run();

  return abortController;
};
